import React, { useEffect, useRef } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Components } from 'react-markdown';
import { ArrowLeft, Calendar, User, Tag } from 'lucide-react';
import Navbar from '../Navbar';
import Footer from '../Footer';
import SEO from '../seo/SEO';
import BlogPostCta from './BlogPostCta';
import BlogPostRecommendations from './BlogPostRecommendations';
import MicroQuiz from './MicroQuiz';
import InteractiveChecklist from './InteractiveChecklist';
import TelegramComments from './TelegramComments';
import { getPostBySlug, formatDate, getPostRecommendations } from '../../lib/posts';
import { stagger, fadeIn, fadeInUp } from '../../lib/animations';
import { extractContentSegments } from '../../lib/contentSegments';

/**
 * Mapeamento de elementos Markdown para componentes com classes Tailwind
 * alinhadas ao design system do Gas2 (fundo escuro, texto slate, acento amber).
 */
// Função utilitária para extrair o ID do vídeo do YouTube
function getYouTubeId(url?: string): string | null {
  if (!url) return null;
  // Padrões: https://www.youtube.com/watch?v=ID, https://youtu.be/ID
  const ytMatch = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{11})/
  );
  return ytMatch ? ytMatch[1] : null;
}

const mdComponents: Components = {
  h1: ({ children }) => (
    <h1 className="font-display text-3xl sm:text-4xl font-bold text-white mt-10 mb-5 leading-tight print:text-slate-900 print:mt-8">
      {children}
    </h1>
  ),
  h2: ({ children }) => (
    <h2 className="font-display text-2xl font-bold text-white mt-12 mb-4 pb-3 border-b border-slate-800 print:text-slate-900 print:border-slate-300 print:mt-8">
      {children}
    </h2>
  ),
  h3: ({ children }) => (
    <h3 className="font-display text-xl font-semibold text-slate-100 mt-8 mb-3 print:text-slate-900 print:mt-6">
      {children}
    </h3>
  ),
  h4: ({ children }) => (
    <h4 className="font-display text-lg font-semibold text-slate-200 mt-6 mb-2 print:text-slate-800 print:mt-4">
      {children}
    </h4>
  ),
  p: ({ children }) => (
    <p className="text-slate-300 leading-relaxed text-base sm:text-[17px] mb-5 print:text-slate-800 print:text-[12pt] print:leading-[1.55] print:mb-3">
      {children}
    </p>
  ),
  a: ({ href, children }) => {
    const ytId = getYouTubeId(href);
    if (ytId) {
      return (
        <div className="my-8">
          <div className="aspect-video w-full rounded-2xl overflow-hidden shadow-2xl border border-slate-800 bg-black print:hidden">
            <iframe
              src={`https://www.youtube.com/embed/${ytId}`}
              title="YouTube video player"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              className="w-full h-full"
              loading="lazy"
            />
          </div>
          <p className="hidden print:block text-[10pt] text-slate-700">
            Video: {href}
          </p>
        </div>
      );
    }
    return (
      <a
        href={href}
        className="text-amber-400 hover:text-amber-300 underline underline-offset-2 transition-colors print:text-slate-900 print:decoration-slate-500 print:break-all"
        target={href?.startsWith('http') ? '_blank' : undefined}
        rel={href?.startsWith('http') ? 'noreferrer noopener' : undefined}
      >
        {children}
      </a>
    );
  },
  strong: ({ children }) => (
    <strong className="text-white font-semibold print:text-slate-900">{children}</strong>
  ),
  em: ({ children }) => <em className="text-slate-300 italic print:text-slate-800">{children}</em>,
  ul: ({ children }) => (
    <ul className="list-disc list-outside pl-6 text-slate-300 mb-5 space-y-2 print:text-slate-800 print:mb-3 print:space-y-1">
      {children}
    </ul>
  ),
  ol: ({ children }) => (
    <ol className="list-decimal list-outside pl-6 text-slate-300 mb-5 space-y-2 print:text-slate-800 print:mb-3 print:space-y-1">
      {children}
    </ol>
  ),
  li: ({ children }) => <li className="text-slate-300 leading-relaxed print:text-slate-800">{children}</li>,
  blockquote: ({ children }) => (
    <blockquote className="border-l-4 border-amber-400 pl-5 my-6 text-slate-400 italic bg-slate-900/60 py-3 pr-4 rounded-r-xl print:bg-transparent print:text-slate-700 print:border-slate-400 print:my-4">
      {children}
    </blockquote>
  ),
  // inline code vs fenced code block
  code: ({ className, children }) => {
    const isBlock = !!className?.includes('language-');
    return isBlock ? (
      <code className={`${className ?? ''} text-amber-200 text-sm print:text-slate-900`}>
        {children}
      </code>
    ) : (
      <code className="bg-slate-800 text-amber-300 px-1.5 py-0.5 rounded text-[0.85em] font-mono print:bg-slate-100 print:text-slate-900 print:border print:border-slate-300">
        {children}
      </code>
    );
  },
  pre: ({ children }) => (
    <pre className="bg-slate-900 border border-slate-700 rounded-xl p-5 overflow-x-auto mb-6 text-sm leading-relaxed print:bg-slate-50 print:border-slate-300 print:text-slate-900 print:p-3 print:rounded-md">
      {children}
    </pre>
  ),
  hr: () => <hr className="border-slate-700 my-10 print:border-slate-300 print:my-6" />,
  img: ({ src, alt }) => (
    <figure className="my-8">
      <img
        src={src}
        alt={alt}
        className="rounded-xl w-full object-cover shadow-xl print:rounded-md print:shadow-none"
        loading="lazy"
      />
      {alt && (
        <figcaption className="text-center text-slate-500 text-sm mt-2 italic print:text-slate-600 print:text-[10pt]">
          {alt}
        </figcaption>
      )}
    </figure>
  ),
  table: ({ children }) => (
    <div className="overflow-x-auto mb-6 rounded-xl border border-slate-700 print:border-slate-300 print:rounded-md">
      <table className="w-full text-sm text-slate-300 border-collapse print:text-slate-800">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-slate-800 print:bg-slate-100">{children}</thead>,
  th: ({ children }) => (
    <th className="px-4 py-3 text-left text-white font-semibold border-b border-slate-700 print:text-slate-900 print:border-slate-300 print:py-2">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="px-4 py-3 border-b border-slate-800 last:border-0 print:border-slate-200 print:py-2">
      {children}
    </td>
  ),
};

/** Página de exibição de um post individual (/blog/:slug) */
const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const previousSlugRef = useRef<string | undefined>(undefined);
  const post = slug ? getPostBySlug(slug) : undefined;
  const recommendations = post ? getPostRecommendations(post.slug) : [];
  const hasRecommendations = recommendations.length > 0;
  const segments = extractContentSegments(post?.content ?? '');

  useEffect(() => {
    if (!slug) return;

    const previousSlug = previousSlugRef.current;
    previousSlugRef.current = slug;

    // Rola suavemente ao trocar entre artigos na mesma tela (ex.: recomendações).
    if (previousSlug && previousSlug !== slug) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [slug]);

  // Redireciona para /blog se o slug não existir
  if (!post) return <Navigate to="/blog" replace />;

  return (
    <>
      <SEO
        title={`${post.title} – Blog Gas2`}
        description={post.tldr}
        canonicalPath={`/blog/${post.slug}`}
        image={post.thumbnailUrl}
        type="article"
        publishedTime={post.date}
        modifiedTime={post.lastEdit}
        author={post.author}
        tags={post.tags}
      />

      <div className="print:hidden">
        <Navbar solid />
      </div>

      <article className="print-article min-h-screen bg-slate-950 print:min-h-0 print:bg-white print:text-slate-900">
        {/* Hero do post */}
        <motion.section
          variants={fadeIn}
          initial="hidden"
          animate="visible"
          className="pt-28 pb-10 px-4 sm:px-6 lg:px-8 print:pt-0 print:pb-6"
        >
          <div className="mx-auto max-w-3xl print:max-w-none print:w-full">
            {/* Voltar ao blog */}
            <Link
              to="/blog"
              className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-amber-400 transition-colors mb-8 print:hidden"
            >
              <ArrowLeft className="h-4 w-4" strokeWidth={2} />
              Voltar ao Blog
            </Link>

            {/* Tags */}
            {post.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-5">
                {post.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/blog?tag=${encodeURIComponent(tag)}`}
                    className="flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-amber-400/10 text-amber-400 border border-amber-400/20 print:bg-transparent print:text-slate-700 print:border-slate-300"
                  >
                    <Tag className="h-3 w-3 shrink-0" strokeWidth={2} />
                    {tag}
                  </Link>
                ))}
              </div>
            )}

            {/* Título principal */}
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-5 print:text-slate-900 print:text-[26pt] print:leading-tight">
              {post.title}
            </h1>

            {/* TLDR em destaque */}
            <p className="text-slate-300 text-lg leading-relaxed mb-7 border-l-4 border-amber-400 pl-4 italic print:text-slate-800 print:border-slate-400 print:text-[12pt]">
              {post.tldr}
            </p>

            {/* Metadados */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-slate-400 pb-8 border-b border-slate-800 print:text-slate-700 print:border-slate-300 print:pb-4">
              <span className="flex items-center gap-1.5">
                <User className="h-4 w-4 shrink-0 print:text-slate-700" strokeWidth={1.8} />
                {post.author}
              </span>
              <span className="flex items-center gap-1.5">
                <Calendar className="h-4 w-4 shrink-0 print:text-slate-700" strokeWidth={1.8} />
                Publicado em {formatDate(post.date)}
              </span>
              {post.lastEdit && (
                <span className="text-slate-500 text-xs print:text-slate-600">
                  Atualizado em {formatDate(post.lastEdit)}
                </span>
              )}
            </div>
          </div>
        </motion.section>

        <div
          className={`mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pb-10 print:max-w-none print:w-full print:px-0 print:pb-0 ${
            hasRecommendations ? 'lg:grid lg:grid-cols-[minmax(0,1fr)_22rem] lg:items-start lg:gap-10' : ''
          }`}
        >
          <div className="min-w-0 lg:max-w-3xl print:max-w-none">
            {/* Thumbnail */}
            {post.thumbnailUrl && (
              <motion.div
                variants={fadeInUp}
                initial="hidden"
                animate="visible"
                className="mb-10 print:mb-6"
              >
                <img
                  src={post.thumbnailUrl}
                  alt={post.title}
                  className="rounded-2xl w-full object-cover aspect-video shadow-2xl print:rounded-md print:shadow-none"
                />
              </motion.div>
            )}

            {/* Post content — rendered as ordered segments (markdown, quiz, checklist…) */}
            <motion.section
              variants={stagger}
              initial="hidden"
              animate="visible"
              className="print-article-content pb-10 print:pb-0"
            >
              {segments.map((segment, i) => {
                if (segment.type === 'markdown') {
                  return (
                    <ReactMarkdown key={i} remarkPlugins={[remarkGfm]} components={mdComponents}>
                      {segment.content}
                    </ReactMarkdown>
                  );
                }
                if (segment.type === 'quiz') {
                  return (
                    <MicroQuiz
                      key={i}
                      slug={post.slug}
                      quiz={segment.data}
                      className="my-8 print:hidden"
                    />
                  );
                }
                if (segment.type === 'checklist') {
                  return (
                    <InteractiveChecklist
                      key={i}
                      slug={post.slug}
                      data={segment.data}
                      anchorIndex={segment.anchorIndex}
                      className="my-8 print:hidden"
                    />
                  );
                }
                return null;
              })}
            </motion.section>
          </div>

          {hasRecommendations && (
            <div className="hidden lg:block print:hidden">
              <BlogPostRecommendations recommendations={recommendations} className="mt-0" />
            </div>
          )}
        </div>

        {/* Widget de comentários do Telegram */}
        <div className="print:hidden">
          <TelegramComments slug={post.slug} />
        </div>

        {/* CTA Download */}
        <div className="print:hidden">
          <BlogPostCta slug={post.slug} />
        </div>

        {hasRecommendations && (
          <div className="lg:hidden print:hidden">
            <BlogPostRecommendations
              recommendations={recommendations}
              className="mx-auto max-w-3xl px-4 sm:px-6 pb-20"
            />
          </div>
        )}
      </article>

      <div className="print:hidden">
        <Footer />
      </div>
    </>
  );
};

export default BlogPost;

